/**
 * Avatar de una persona: el mismo color y las mismas iniciales en la barra lateral, el
 * perfil y la lista de miembros.
 *
 * Antes la barra lateral pintaba la `picture` de Auth0, que para quien no tiene Gravatar
 * es una imagen genérica gris con las iniciales del correo («RG» de rgarcia), mientras el
 * perfil pintaba «RA» en lavanda. Misma persona, dos avatares.
 */

// Paleta apagada del sistema (05-10): la de las familias de datos y la severidad de sgv2.
// Iniciales en negro sobre el pastel.
const AVATAR_GRADIENTS: [string, string][] = [
  ['#A8C5F0', '#8FB2E6'], // azul (acento)
  ['#C5AFEC', '#AF97DE'], // lavanda (familia IdP)
  ['#7FE3B0', '#66CF9A'], // verde (sev-low)
  ['#D4B85A', '#C2A548'], // ámbar (sev-med)
  ['#E28B4F', '#D0783C'], // naranja (sev-high)
  ['#E5E7EB', '#C9CDD3'], // gris claro (familia DLP)
];

/** Gradiente determinista a partir de un seed (el email): misma persona, mismo color. */
export function avatarGradientForSeed(seed: string): string {
  const normalized = (seed || '').trim().toLowerCase();
  let hash = 0;
  for (let i = 0; i < normalized.length; i++) {
    hash = (hash * 31 + normalized.charCodeAt(i)) | 0;
  }
  const [from, to] = AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${from} 0%, ${to} 100%)`;
}

/**
 * La foto de Auth0 solo si es una foto de verdad.
 *
 * Se descartan la imagen genérica de Auth0 (`cdn.auth0.com/avatars/…`, unas iniciales) y los
 * Gravatar: Auth0 los pone por defecto con esa misma imagen de respaldo, y las cuentas
 * corporativas no tienen Gravatar. Pedirlos con `d=404` para caer a las iniciales funcionaba,
 * pero dejaba un 404 en consola en cada carga. Quedan las fotos de los proveedores sociales
 * (Google, Microsoft…).
 */
export function avatarPicture(picture: string | null | undefined): string | undefined {
  if (!picture) return undefined;
  try {
    const host = new URL(picture).hostname;
    if (/(^|\.)gravatar\.com$/i.test(host)) return undefined;
    if (/^cdn\.auth0\.com$/i.test(host)) return undefined;
  } catch {
    return undefined;
  }
  return picture;
}
